import { Search } from 'lucide-react';
import type { DashboardCounts, CodebaseResponse, HealthResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

type DateRange = 'today' | '7d' | '30d' | 'all';

interface StatusSummaryBarProps {
  counts: DashboardCounts;
  activeFilter: string | null;
  onFilterChange: (status: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  projectFilter: string | null;
  onProjectFilterChange: (codebaseId: string | null) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  codebases: CodebaseResponse[] | undefined;
  health: HealthResponse | undefined;
}

const STATUS_CHIPS = ['running', 'paused', 'completed', 'failed', 'cancelled', 'pending'] as const;

const STATUS_DOT: Record<(typeof STATUS_CHIPS)[number], string> = {
  running: 'bg-accent-bright shadow-[0_0_6px_var(--accent-bright)]',
  paused: 'bg-warning',
  pending: 'bg-text-tertiary',
  completed: 'bg-success',
  failed: 'bg-error',
  cancelled: 'bg-text-tertiary',
};

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

export function StatusSummaryBar({
  counts,
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  projectFilter,
  onProjectFilterChange,
  dateRange,
  onDateRangeChange,
  codebases,
  health,
}: StatusSummaryBarProps): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      {/* Row 1: Status cells — Flight Deck strip */}
      <div className="flex divide-x divide-border border-b border-border">
        <button
          onClick={(): void => {
            onFilterChange(null);
          }}
          className={cn(
            'flex-1 px-3 py-2.5 text-left transition-colors',
            activeFilter === null
              ? 'border-b-2 border-accent-bright bg-[oklch(0.72_0.14_225/0.08)]'
              : 'border-b-2 border-transparent hover:bg-surface-elevated'
          )}
        >
          <span className="block font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
            All
          </span>
          <span
            className={cn(
              'block text-2xl font-semibold leading-none tracking-tight',
              activeFilter === null ? 'text-accent-bright' : 'text-text-primary'
            )}
          >
            {counts.all}
          </span>
        </button>
        {STATUS_CHIPS.map(status => {
          const count = counts[status];
          const isActive = activeFilter === status;
          return (
            <button
              key={status}
              onClick={(): void => {
                onFilterChange(isActive ? null : status);
              }}
              className={cn(
                'flex-1 px-3 py-2.5 text-left transition-colors',
                isActive
                  ? 'border-b-2 border-accent-bright bg-[oklch(0.72_0.14_225/0.08)]'
                  : 'border-b-2 border-transparent hover:bg-surface-elevated'
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className={cn('size-1.5 rounded-full', STATUS_DOT[status])} />
                <span className="font-mono text-[10.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                  {status}
                </span>
              </div>
              <span
                className={cn(
                  'mt-0.5 block text-2xl font-semibold leading-none tracking-tight',
                  isActive ? 'text-accent-bright' : 'text-text-primary'
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Row 2: Codebase dropdown, date range, search, capacity */}
      <div className="flex flex-wrap items-center gap-3 bg-surface-inset px-4 py-2.5">
        <select
          value={projectFilter ?? ''}
          onChange={(e): void => {
            onProjectFilterChange(e.target.value || null);
          }}
          className="rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-text-primary focus:border-primary focus:outline-none"
        >
          <option value="">All Codebases</option>
          {codebases?.map(cb => (
            <option key={cb.id} value={cb.id}>
              {cb.name}
            </option>
          ))}
        </select>

        <select
          value={dateRange}
          onChange={(e): void => {
            onDateRangeChange(e.target.value as DateRange);
          }}
          className="rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-text-primary focus:border-primary focus:outline-none"
        >
          {DATE_RANGE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e): void => {
              onSearchChange(e.target.value);
            }}
            placeholder="Search workflows…"
            className="w-full rounded-md border border-border bg-surface-elevated py-1.5 pl-7 pr-2 text-xs text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
          />
        </div>

        {health && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[oklch(0.72_0.16_155/0.25)] bg-[oklch(0.72_0.16_155/0.1)] px-2 py-0.5 font-mono text-[11px] text-success">
            <span className="size-1.5 rounded-full bg-success shadow-[0_0_6px_var(--success)]" />
            OK · {String(health.concurrency.active)}/{String(health.concurrency.maxConcurrent)}{' '}
            active
          </span>
        )}
      </div>
    </div>
  );
}
